import { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';

// T132 — module-level subscription + 60s poll for the Activity feed, mirroring
// useNavCounts.js: a single shared cache + a Set of listeners keeps every
// mounted instance (desktop rail + mobile drawer) in sync without a Context.
const POLL_MS = 60_000;
// Must not exceed the server's per-request cap in backend/src/routes/activity.js.
const MARK_CHUNK = 50;
const _listeners = new Set();
let _cache = { items: [], unreadCount: 0, total: 0, hasMore: false, loaded: false };

// See resetNavCounts() — same race, and here the stale answer carries the other
// account's job titles, not just a count.
let _epoch = 0;

async function _fetch(append = false) {
  const epoch = _epoch;
  try {
    const offset = append ? _cache.items.length : 0;
    const body = await api.get(`/activity?limit=30&offset=${offset}`);
    const incoming = body.items ?? [];
    const items = append
      ? [...(_cache.items ?? []), ...incoming.filter(
          (x) => !new Set((_cache.items ?? []).map((p) => p.id)).has(x.id),
        )]
      : incoming;
    if (epoch !== _epoch) return;
    _cache = {
      items,
      unreadCount: body.unreadCount ?? 0,
      total: body.total ?? items.length,
      hasMore: !!body.hasMore,
      loaded: true,
    };
    _listeners.forEach((fn) => fn(_cache));
  } catch {
    /* sidebar renders 0 — no toast, mirrors useNavCounts */
  }
}

// The cheap badge poll: a count, not a page of rows.
async function _pollBadge() {
  const epoch = _epoch;
  try {
    const body = await api.get('/activity/unread-count');
    if (epoch !== _epoch) return;
    _cache = { ..._cache, unreadCount: body.unreadCount ?? 0 };
    _listeners.forEach((fn) => fn(_cache));
  } catch {
    /* sidebar keeps its last number — no toast, mirrors useNavCounts */
  }
}

// T290 — this cache lives at module scope and logout only calls `setUser(null)`,
// with no page reload, so without this the next account to sign in on the same
// tab paints the previous one's activity rows before its own fetch lands. Three
// people now have accounts on this instance; one of them seeing another's job
// titles, even for a frame, is not something to leave to timing.
export function resetActivityCache() {
  _epoch += 1;
  _cache = { items: [], unreadCount: 0, total: 0, hasMore: false, loaded: false };
  _listeners.forEach((fn) => fn(_cache));
}

export function useActivity() {
  const [state, setState] = useState(_cache);
  useEffect(() => {
    _listeners.add(setState);
    if (!_cache.loaded) _fetch(false);
    // T281 — the poll used to be `setInterval(_fetch, POLL_MS)`. setInterval
    // calls with no arguments, so `append` was undefined and every minute the
    // panel silently collapsed back to page one, discarding anything Load more
    // had fetched. The badge is the only thing that needs a poll, and there has
    // been a route for exactly that since T132 which nothing ever called.
    // T438 — same rule as the nav counts: a hidden tab polls nothing, and
    // becoming visible fetches once rather than waiting out the interval.
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      _pollBadge();
    };
    const t = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') _pollBadge();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      _listeners.delete(setState);
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const refresh = useCallback(() => _fetch(false), []);
  const loadMore = useCallback(() => _fetch(true), []);
  // T281 — exactly the unread rows the panel is holding, by id.
  //
  // The first attempt sent the oldest row's timestamp and let the server mark
  // everything at or after it. That reintroduced the original bug in the one
  // state where it hurts most: an empty cache — what you have after a failed
  // fetch — sent no bound, and the server marked all 327.
  //
  // Called whenever the held rows change while the drawer is open, so a page
  // revealed by Load more is marked too; a badge that keeps counting rows the
  // operator has read is the same lie in the other direction.
  //
  // T281 review — CHUNKED, and the rows are marked locally on success. The
  // server caps a request at MARK_CHUNK ids, and the client accumulates pages,
  // so a flat send silently truncated: after two Load more clicks the same
  // first 50 ids kept winning the cap and rows 51+ could never be marked at
  // all. Marking locally is what keeps each batch small — without it every
  // call re-sent every id the panel had ever shown.
  const markRead = useCallback(async () => {
    // T290c — the epoch guard that the two fetches above carry belongs here too.
    // This awaits network round-trips and then writes the module cache, so an
    // account ending mid-batch would otherwise have its rows re-seeded into the
    // cleared cache — with `readAt` stamped on them, which is worse than the
    // fetches' version because it also asserts the next person read them.
    const epoch = _epoch;
    const pending = (_cache.items ?? []).filter((a) => !a.readAt).map((a) => a.id);
    if (pending.length === 0) return;
    const done = new Set();
    let marked = 0;
    try {
      for (let i = 0; i < pending.length; i += MARK_CHUNK) {
        const ids = pending.slice(i, i + MARK_CHUNK);
        const body = await api.post('/activity/mark-read', { ids });
        marked += body?.marked ?? 0;
        ids.forEach((id) => done.add(id));
      }
    } catch {
      /* keep whatever the completed chunks achieved; the badge stays honest */
    }
    if (done.size === 0 || epoch !== _epoch) return;
    const at = new Date().toISOString();
    _cache = {
      ..._cache,
      // The count comes back from the writes, not from an assumption about them.
      unreadCount: Math.max(0, (_cache.unreadCount ?? 0) - marked),
      items: (_cache.items ?? []).map((a) => (done.has(a.id) && !a.readAt ? { ...a, readAt: at } : a)),
    };
    _listeners.forEach((fn) => fn(_cache));
  }, []);

  return { ...state, refresh, markRead, loadMore };
}
